import { useCallback, useEffect, useState } from 'react';
import { parseQuerifiedFiltersForRequest } from 'src/page-components/search/utils';
import useLocationStore from 'src/hooks/useLocationStore';
import debounce from 'lodash/debounce';
import useStoreDeep from 'src/hooks/useStore';
import {
  defaultPageSize,
  PostDrugRequest,
  PostDrugResponse
} from '../requests/postDrugSearch';
import postDrugSearchFilters from '../requests/postDrugSearchFilters';
import {
  APIError,
  APIHookResponse,
  BlogSortBy,
  SortBy,
  SortDirection
} from '../types';

interface PostSearchFiltersResponse extends APIHookResponse {
  data: PostDrugResponse;
}

export const getSearchRequestObject = (
  pageNumber: number,
  search?: string | string[],
  filters?: string | string[],
  sortBy?: string | string[] | SortBy | BlogSortBy,
  sortDirection?: string | string[] | SortDirection,
  pageSize?: number
): PostDrugRequest => {
  return {
    textSearch: (search as string) ?? null,
    pageNumber: pageNumber ?? null,
    sortBy: (sortBy as SortBy) ?? null,
    sortDirection: (sortDirection as SortDirection) ?? null,
    filters: parseQuerifiedFiltersForRequest((filters as string) ?? null),
    pageSize: pageSize ?? defaultPageSize
  };
};

const usePostSearchFilters = (
  requestObj: PostDrugRequest
): PostSearchFiltersResponse => {
  const userLocation = useStoreDeep(
    useLocationStore,
    (state) => state.userLocation
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<APIError>(null);
  const [data, setData] = useState<PostDrugResponse>(null);

  const debouncedLoadFilters = useCallback(
    debounce(async (request: PostDrugRequest) => {
      try {
        const response = await postDrugSearchFilters(request);
        setData(response);
        return response;
      } catch (err) {
        setError(err);
        return null;
      } finally {
        setIsLoading(false);
      }
    }, 250),
    []
  );

  useEffect(() => {
    if (userLocation?.name && requestObj) {
      setIsLoading(true);
      debouncedLoadFilters(requestObj);
    }
  }, [
    requestObj?.textSearch,
    requestObj?.pageSize,
    JSON.stringify(requestObj?.filters),
    requestObj?.sortBy,
    requestObj?.sortDirection,
    userLocation?.name
  ]);

  return {
    isLoading,
    error,
    data
  };
};

export default usePostSearchFilters;
